import React from 'react';

import { UpgradeModalProvider } from '@zapier/upsells/src/contexts';
import { UpgradeReason } from '@zapier/upsells/src/types';

import { UpgradeModalLayout } from './UpgradeModalLayout';
import { useUpgradeModal } from './useUpgradeModal';

export type UpgradeModalProps = {
  /**
   * If the upgrade reason is to give the user access to a particular app, provide that app name here.
   */
  appName?: string;
  /**
   * Called when the user closes the modal.
   */
  onClose: () => void;
  /**
   * Called when the user completes the upgrade process.
   */
  onUpgradeFinish?: () => void;
  /**
   * If `shouldAutoUpgrade` is false, then the upgrade modal will indicate that a new
   * trial will be started, but does not collect any payment information nor indicate
   * that any form of payment will be required.
   */
  shouldAutoUpgrade?: boolean;
  /**
   * The reason why the user is being prompted to upgrade.
   */
  upgradeReason: UpgradeReason;
  /**
   * Used to render content that's specific to a user trying to turn on a Zap.
   */
  wasTriggeredByZapToggle?: boolean;
};

/**
 * Additional documentation around the inline upgrade system can be found [in Coda](https://coda.io/d/Revenue-Growth_d_s-Io-GBfk/Inline-Upgrades).
 */
export const UpgradeModal = ({
  appName,
  onClose,
  onUpgradeFinish = () => {},
  shouldAutoUpgrade = true,
  upgradeReason,
  wasTriggeredByZapToggle = false,
}: UpgradeModalProps) => {
  const upgradeModalData = useUpgradeModal({
    shouldAutoUpgrade,
    upgradeReason,
  });

  return (
    <UpgradeModalProvider upgradeModalData={upgradeModalData}>
      <UpgradeModalLayout
        appName={appName}
        onClose={onClose}
        onUpgradeFinish={onUpgradeFinish}
        shouldAutoUpgrade={shouldAutoUpgrade}
        upgradeReason={upgradeReason}
        wasTriggeredByZapToggle={wasTriggeredByZapToggle}
      />
    </UpgradeModalProvider>
  );
};
